import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HeroSectionProps {
  content: {
    title?: string;       heading?: string;
    subtitle?: string;    subheading?: string;
    ctaText?: string;     ctaLabel?: string;
    ctaLink?: string;     ctaHref?: string;
    imageUrl?: string;
    secondaryCtaText?: string;
    secondaryCtaLink?: string;
    overlayOpacity?: number;
  };
  variant?: string;
  layoutStyle?: string;
}

export function HeroSection({ content, variant = 'default', layoutStyle = 'classic' }: HeroSectionProps) {
  const {
    title,
    heading,
    subtitle,
    subheading,
    ctaText,
    ctaLabel,
    ctaLink,
    ctaHref,
    imageUrl,
    secondaryCtaText,
    secondaryCtaLink,
    overlayOpacity = 60,
  } = content;

  // Accept both naming conventions (page builder uses title/subtitle, provision_org uses heading/subheading)
  const resolvedTitle = title || heading || 'Your Headline Here';
  const resolvedSubtitle = subtitle || subheading || 'Add a compelling description';
  const resolvedCtaText = ctaText || ctaLabel || 'Get Started';
  const resolvedCtaLink = ctaLink || ctaHref || '/booking';

  const isMinimal = layoutStyle === 'modern-minimal' || layoutStyle === 'elegant-serif';
  const isBold = layoutStyle === 'bold-geometric';
  const isPlayful = layoutStyle === 'playful-rounded';

  // Variant: Centered (text centered, image below)
  if (variant === 'centered') {
    return (
      <section className="relative pt-20 pb-24 md:pt-32 md:pb-32 overflow-hidden bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h1 className={cn(
              "font-display font-extrabold text-foreground leading-tight",
              isMinimal ? "text-5xl md:text-6xl lg:text-7xl tracking-tight" : "text-4xl md:text-5xl lg:text-6xl"
            )}>
              {resolvedTitle}
            </h1>
            <p className={cn(
              "text-muted-foreground max-w-xl mx-auto",
              isMinimal ? "text-xl md:text-2xl font-light" : "text-lg"
            )}>
              {resolvedSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="hero" size="xl" className="group" asChild>
                <Link to={resolvedCtaLink}>
                  {resolvedCtaText}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              {secondaryCtaText && secondaryCtaLink && (
                <Button variant="outline" size="xl" asChild>
                  <Link to={secondaryCtaLink}>{secondaryCtaText}</Link>
                </Button>
              )}
            </div>
          </div>
          {imageUrl && (
            <div className="mt-12 max-w-4xl mx-auto">
              <img 
                src={imageUrl} 
                alt="" 
                className={cn(
                  "w-full object-cover shadow-xl",
                  isMinimal ? "rounded-none" : "rounded-3xl"
                )}
              />
            </div>
          )}
        </div>
      </section>
    );
  }

  // Variant: Fullwidth (full background image with overlay)
  if (variant === 'fullwidth') {
    return (
      <section 
        className="relative min-h-[80vh] flex items-center overflow-hidden"
        style={{
          backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div 
          className="absolute inset-0 bg-foreground" 
          style={{ opacity: overlayOpacity / 100 }}
        />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl space-y-6">
            <h1 className={cn(
              "font-display font-extrabold text-white leading-tight",
              isBold ? "text-5xl md:text-7xl uppercase tracking-wide" : "text-4xl md:text-6xl"
            )}>
              {resolvedTitle}
            </h1>
            <p className="text-xl text-white/90">
              {resolvedSubtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                variant="secondary" 
                size="xl" 
                className={cn(
                  "group bg-white text-foreground hover:bg-white/90",
                  isBold && "rounded-none uppercase tracking-wider"
                )} 
                asChild
              >
                <Link to={resolvedCtaLink}>
                  {resolvedCtaText}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              {secondaryCtaText && secondaryCtaLink && (
                <Button 
                  variant="outline" 
                  size="xl" 
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link to={secondaryCtaLink}>{secondaryCtaText}</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Variant: Minimal (text only, no image)
  if (variant === 'minimal') {
    return (
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="font-display font-extrabold text-foreground leading-tight text-5xl md:text-6xl lg:text-7xl tracking-tight mb-6">
              {resolvedTitle}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mb-8">
              {resolvedSubtitle}
            </p>
            <Button variant="hero" size="xl" className="group" asChild>
              <Link to={resolvedCtaLink}>
                {resolvedCtaText}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // Variant: Image Left (reverse of default)
  if (variant === 'image-left') {
    return (
      <section className={cn(
        "relative pt-14 pb-16 md:pt-28 md:pb-20 overflow-hidden bg-background",
        isPlayful && "bg-gradient-to-br from-background via-secondary/30 to-background"
      )}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {imageUrl && (
              <div className="relative order-2 md:order-1">
                <div className={cn(
                  "aspect-[4/3] overflow-hidden shadow-xl",
                  isPlayful ? "rounded-[2rem]" : "rounded-3xl"
                )}>
                  <img 
                    src={imageUrl} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
            <div className="space-y-8 order-1 md:order-2">
              <div className="space-y-4">
                <h1 className={cn(
                  "font-display font-extrabold text-foreground leading-tight",
                  isPlayful ? "text-4xl md:text-5xl" : "text-4xl md:text-5xl lg:text-6xl"
                )}>
                  {resolvedTitle}
                </h1>
                <p className="text-lg text-muted-foreground max-w-md">
                  {resolvedSubtitle}
                </p>
              </div>
              <Button 
                variant="hero" 
                size="xl" 
                className={cn("group", isPlayful && "rounded-full")} 
                asChild
              >
                <Link to={resolvedCtaLink}>
                  {resolvedCtaText}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default: Split layout (text left, image right)
  return (
    <section className={cn(
      "relative pt-14 pb-16 md:pt-28 md:pb-20 overflow-hidden bg-background",
      isPlayful && "bg-gradient-to-br from-background via-secondary/30 to-background"
    )}>
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className={cn(
                "font-display font-extrabold text-foreground leading-tight",
                isPlayful ? "text-4xl md:text-5xl" : "text-4xl md:text-5xl lg:text-6xl"
              )}>
                {resolvedTitle}
              </h1>
              <p className="text-lg text-muted-foreground max-w-md">
                {resolvedSubtitle}
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button 
                variant="hero" 
                size="xl" 
                className={cn("group", isPlayful && "rounded-full")} 
                asChild
              >
                <Link to={resolvedCtaLink}>
                  {resolvedCtaText}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              {secondaryCtaText && secondaryCtaLink && (
                <Button variant="outline" size="xl" asChild>
                  <Link to={secondaryCtaLink}>{secondaryCtaText}</Link>
                </Button>
              )}
            </div>
          </div>

          {imageUrl && (
            <div className="relative">
              <div className={cn(
                "aspect-[4/3] overflow-hidden shadow-xl",
                isPlayful ? "rounded-[2rem]" : "rounded-3xl"
              )}>
                <img 
                  src={imageUrl} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}